import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
    Sparkles,
    Trophy,
    Flame,
    Users,
    Dumbbell,
    MapPin,
    Zap,
    Target,
    ChevronRight,
    Activity,
    Crown,
    Play,
    Instagram,
    Heart,
    Calendar,
} from "lucide-react";

import { PhoneMockup, RawPhoneImage } from "../components/PhoneMockup";
import Navbar from "../components/Navbar";
import WaitlistForm from "../components/WaitlistForm";
import VoroLogo from "../components/VoroLogo";
import ChallengeShowcase from "../components/ChallengeShowcase";

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREENSHOTS = {
    map: "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/r7iyylqn_image.png",
    community:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/iorvdxxi_12.png",
    partners:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/dhpawbu1_image1%20%281%29.png",
    gymLeaderboard:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/dfmopd7m_image0%20%284%29.png",
    userLeaderboard:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/m0ot6jmy_167.jpeg",
    athleteProfile:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/n9ftym5k_image0%20%285%29.png",
    events:
        "https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/5pec616t_image1.png",
};

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] },
};

const PurpleButton = ({ children, onClick, className = "", testId, asLink, href }) => {
    const cls = `group inline-flex items-center gap-2 rounded-full px-7 py-4 text-white font-semibold transition-all hover:scale-[1.03] shadow-[0_0_50px_rgba(139,92,246,0.5)] ${className}`;
    const style = {
        background: "linear-gradient(135deg, #a855f7 0%, #8b5cf6 50%, #7c3aed 100%)",
    };
    if (asLink) {
        return (
            <a href={href} className={cls} style={style} data-testid={testId}>
                {children}
            </a>
        );
    }
    return (
        <button onClick={onClick} className={cls} style={style} data-testid={testId}>
            {children}
        </button>
    );
};

const GhostButton = ({ children, onClick, testId, className = "" }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full px-6 py-4 border border-white/15 bg-white/[0.04] backdrop-blur hover:bg-white/[0.08] text-white/90 font-medium transition-colors ${className}`}
    >
        {children}
    </button>
);

const SectionEyebrow = ({ children, icon: Icon }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/10 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-violet-200">
        {Icon && <Icon size={12} />}
        {children}
    </div>
);

const FeatureBullet = ({ children }) => (
    <li className="flex items-start gap-2.5 text-white/75">
        <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-violet-400 shadow-[0_0_10px_rgba(139,92,246,0.8)]" />
        <span className="text-[15px] leading-relaxed">{children}</span>
    </li>
);

/* ---------- Feature split (alternating screenshot sections) ---------- */
const FeatureSplit = ({
    id,
    eyebrow,
    eyebrowIcon,
    title,
    accent,
    description,
    bullets,
    children,
    reverse,
    testId,
}) => (
    <section id={id} data-testid={testId} className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div
                className={`grid lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
                    reverse ? "lg:[&>div:first-child]:order-2" : ""
                }`}
            >
                <motion.div {...fadeUp} className="lg:col-span-6">
                    <SectionEyebrow icon={eyebrowIcon}>{eyebrow}</SectionEyebrow>
                    <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-[3.6rem] font-extrabold text-white tracking-tight leading-[1.02]">
                        {title}{" "}
                        {accent && (
                            <span className="bg-gradient-to-br from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                                {accent}
                            </span>
                        )}
                    </h2>
                    <p className="mt-5 text-white/65 text-lg md:text-xl max-w-xl leading-relaxed">
                        {description}
                    </p>
                    {bullets && (
                        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
                            {bullets.map((b, i) => (
                                <FeatureBullet key={i}>{b}</FeatureBullet>
                            ))}
                        </ul>
                    )}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="lg:col-span-6 relative flex justify-center lg:justify-end"
                >
                    <div
                        aria-hidden
                        className="absolute -inset-10 -z-10 opacity-70 blur-3xl"
                        style={{
                            background:
                                "radial-gradient(closest-side, rgba(139,92,246,0.5), transparent 70%)",
                        }}
                    />
                    {children}
                </motion.div>
            </div>
        </div>
    </section>
);

/* ---------- Stat card ---------- */
const StatCard = ({ value, label, accent, Icon, i }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: i * 0.05 }}
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur p-6"
    >
        <Icon size={18} className="text-violet-300" />
        <div className="font-display mt-4 text-3xl md:text-4xl font-extrabold leading-none">
            {value}{" "}
            {accent && (
                <span className="text-base font-bold text-violet-300">{accent}</span>
            )}
        </div>
        <div className="text-sm text-white/60 mt-2">{label}</div>
    </motion.div>
);

/* ---------- Landing page ---------- */
const Landing = () => {
    const [count, setCount] = useState(null);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/waitlist/count`)
            .then((res) => {
                if (mounted) setCount(res.data.displayed ?? res.data.count);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    const scrollToWaitlist = () => {
        const el = document.getElementById("waitlist");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div id="top" className="relative bg-[#050505] text-white min-h-screen overflow-hidden">
            <Navbar onJoinClick={scrollToWaitlist} />

            {/* ==================== HERO ==================== */}
            <section className="relative pt-28 md:pt-36 pb-20 md:pb-28">
                <div
                    className="aurora"
                    style={{
                        width: 700,
                        height: 700,
                        top: -160,
                        right: -160,
                        background: "#7c3aed",
                        opacity: 0.5,
                    }}
                />
                <div
                    className="aurora"
                    style={{
                        width: 500,
                        height: 500,
                        bottom: -160,
                        left: -100,
                        background: "#6d28d9",
                        opacity: 0.35,
                    }}
                />

                <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    {/* Left text */}
                    <div className="lg:col-span-6 lg:pt-4">
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            data-testid="hero-badge"
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur px-3.5 py-1.5 text-xs text-white/80"
                        >
                            <Sparkles size={12} className="text-violet-300" />
                            <span>Partnering with Perth gyms, run clubs &amp; studios</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="font-display mt-6 text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-[5.1rem] xl:text-[5.6rem] font-extrabold tracking-tight"
                        >
                            Fitness Is{" "}
                            <span className="relative inline-block">
                                <span className="bg-gradient-to-br from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                                    Better
                                </span>
                                <span
                                    aria-hidden
                                    className="absolute -inset-2 -z-10 blur-2xl bg-violet-500/30 rounded-full"
                                />
                            </span>{" "}
                            Together
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="mt-6 text-lg md:text-xl text-white/65 max-w-xl leading-relaxed"
                        >
                            Join local fitness communities, discover events, stay
                            accountable, complete challenges, and celebrate progress with
                            the people around you.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
                        >
                            <PurpleButton
                                onClick={scrollToWaitlist}
                                testId="hero-cta-button"
                            >
                                Join Waitlist
                                <ChevronRight
                                    size={18}
                                    className="transition-transform group-hover:translate-x-0.5"
                                />
                            </PurpleButton>
                        </motion.div>
                    </div>

                    {/* Right phones */}
                    <div className="lg:col-span-6 relative">
                        <div className="relative h-[560px] sm:h-[620px] lg:h-[680px]">
                            <div
                                className="aurora"
                                style={{
                                    width: 520,
                                    height: 520,
                                    top: 60,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    background: "#8b5cf6",
                                    opacity: 0.55,
                                }}
                            />

                            {/* Back-left: Home / Active Athletes (Marcus K.) */}
                            <div className="absolute left-0 top-2 z-10 hidden sm:block">
                                <PhoneMockup
                                    src="https://customer-assets.emergentagent.com/job_site-launch-253/artifacts/3w6r7d14_image0%20%286%29.png"
                                    alt="Voro home — Active Athletes"
                                    size="md"
                                    rotate={-10}
                                    floatDelay={0.8}
                                    testId="hero-phone-gym"
                                />
                            </div>

                            {/* Center / front: Live Activity */}
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20">
                                <PhoneMockup
                                    src={SCREENSHOTS.community}
                                    alt="Voro live activity feed"
                                    size="lg"
                                    rotate={0}
                                    floatDelay={0}
                                    testId="hero-phone-feed"
                                />
                            </div>

                            {/* Back-right: Partners+ */}
                            <div className="absolute right-0 top-6 z-10 hidden sm:block">
                                <PhoneMockup
                                    src={SCREENSHOTS.partners}
                                    alt="Voro Partners+"
                                    size="md"
                                    rotate={10}
                                    floatDelay={0.4}
                                    testId="hero-phone-partners"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== SOCIAL PROOF / STATS ==================== */}
            <section
                data-testid="section-social-proof"
                className="relative py-16 md:py-24 border-y border-white/[0.06] bg-white/[0.012]"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <motion.div {...fadeUp} className="max-w-3xl">
                        <SectionEyebrow icon={Flame}>By the numbers</SectionEyebrow>
                        <h2 className="font-display mt-5 text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
                            Built For{" "}
                            <span className="bg-gradient-to-br from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">
                                Local Fitness Communities
                            </span>
                        </h2>
                    </motion.div>

                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <StatCard
                            i={0}
                            Icon={MapPin}
                            value="Perth"
                            label="Launching first"
                        />
                        <StatCard
                            i={1}
                            Icon={Users}
                            value="Local"
                            label="Gyms, clubs & studios"
                        />
                        <StatCard
                            i={2}
                            Icon={Calendar}
                            value="Weekly"
                            label="Events & meetups"
                        />
                        <StatCard
                            i={3}
                            Icon={Heart}
                            value="Daily"
                            label="Community accountability"
                        />
                    </div>
                </div>
            </section>

            {/* ==================== FEATURES INTRO ==================== */}
            <section id="features" data-testid="section-features" className="relative pt-24 md:pt-32 pb-8">
                <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
                    <SectionEyebrow icon={Sparkles}>Features</SectionEyebrow>
                    <motion.h2
                        {...fadeUp}
                        className="font-display mt-5 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.02]"
                    >
                        Everything Your Community Needs To{" "}
                        <span className="bg-gradient-to-br from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">
                            Stay Consistent
                        </span>
                    </motion.h2>
                </div>
            </section>

            {/* ==================== FEATURE 1: MAP ==================== */}
            <FeatureSplit
                id="map"
                testId="section-feature-map"
                eyebrow="Live Community Map"
                eyebrowIcon={MapPin}
                title="Discover Your Local"
                accent="Fitness Community"
                description="See the gyms, run clubs, studios, and athletes training around Perth in real time. Find sessions to join, communities to belong to, and people who train near you."
                bullets={[
                    "Live community map",
                    "Nearby gyms & clubs",
                    "Session discovery",
                    "Local fitness scene",
                ]}
            >
                <PhoneMockup
                    src={SCREENSHOTS.map}
                    alt="Voro live athlete map"
                    size="md"
                    rotate={2}
                    floatDelay={0.2}
                    testId="phone-map"
                />
            </FeatureSplit>

            {/* ==================== FEATURE 2: COMMUNITY FEED ==================== */}
            <FeatureSplit
                id="community"
                testId="section-feature-community"
                eyebrow="Community Feed"
                eyebrowIcon={Activity}
                title="The Community"
                accent="Feed"
                description="Voro is built around the people you train with. Stay motivated by your community — celebrate workouts, cheer on PRs, share progress, and stay accountable together."
                bullets={[
                    "Community moments",
                    "Shared progress",
                    "Club & studio activity",
                    "Event check-ins",
                    "Encouragement in real time",
                ]}
                reverse
            >
                <PhoneMockup
                    src={SCREENSHOTS.community}
                    alt="Voro fitness feed"
                    size="lg"
                    rotate={-3}
                    floatDelay={0.5}
                    testId="phone-community"
                />
            </FeatureSplit>

            {/* ==================== FEATURE 3: EVENTS ==================== */}
            <section id="events" data-testid="section-events" className="relative py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <motion.div {...fadeUp} className="lg:col-span-6">
                            <SectionEyebrow icon={Calendar}>Events</SectionEyebrow>
                            <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.02]">
                                Join Your Local{" "}
                                <span className="bg-gradient-to-br from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">
                                    Fitness Scene
                                </span>
                            </h2>
                            <p className="mt-5 text-white/65 text-lg md:text-xl max-w-xl leading-relaxed">
                                Run clubs, group workouts, gym meetups, pilates pop-ups,
                                HYROX sessions — discover and join events happening near you,
                                or host one of your own.
                            </p>

                            {/* Join / Create dual chips */}
                            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
                                <div
                                    data-testid="events-join-card"
                                    className="rounded-2xl border border-violet-400/30 bg-violet-500/10 px-4 py-4"
                                >
                                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-violet-200">
                                        <Users size={12} /> Join
                                    </div>
                                    <div className="mt-2 font-display text-lg font-extrabold">
                                        Find events nearby
                                    </div>
                                    <div className="text-xs text-white/55 mt-0.5">
                                        Tap, RSVP, show up.
                                    </div>
                                </div>
                                <div
                                    data-testid="events-create-card"
                                    className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4"
                                >
                                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/70">
                                        <Sparkles size={12} className="text-violet-300" /> Create
                                    </div>
                                    <div className="mt-2 font-display text-lg font-extrabold">
                                        Host your own
                                    </div>
                                    <div className="text-xs text-white/55 mt-0.5">
                                        Invite the community.
                                    </div>
                                </div>
                            </div>

                            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
                                {[
                                    "Run clubs",
                                    "Group workouts",
                                    "Pilates & yoga pop-ups",
                                    "HYROX sessions",
                                    "Gym meetups",
                                ].map((b, i) => (
                                    <FeatureBullet key={i}>{b}</FeatureBullet>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="lg:col-span-6 relative flex justify-center lg:justify-end"
                        >
                            <div
                                aria-hidden
                                className="absolute -inset-10 -z-10 opacity-70 blur-3xl"
                                style={{
                                    background:
                                        "radial-gradient(closest-side, rgba(139,92,246,0.5), transparent 70%)",
                                }}
                            />
                            <PhoneMockup
                                src={SCREENSHOTS.events}
                                alt="Voro nearby events"
                                size="lg"
                                rotate={3}
                                floatDelay={0.4}
                                testId="phone-events"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ==================== FEATURE 4: PARTNERS+ (ACCOUNTABILITY) ==================== */}
            <FeatureSplit
                id="partners"
                testId="section-feature-partners"
                eyebrow="Partners+"
                eyebrowIcon={Users}
                title="Stay Accountable With A"
                accent="Training Partner"
                description="Consistency is the hardest part — and it's easier with someone beside you. Match with compatible athletes based on training style, experience, gym, and goals. Build accountability through real-world training partnerships."
                bullets={[
                    "Training style",
                    "Experience level",
                    "Gym location",
                    "Shared goals",
                    "Real accountability",
                ]}
                reverse
            >
                <PhoneMockup
                    src={SCREENSHOTS.partners}
                    alt="Voro Partners+"
                    size="lg"
                    rotate={3}
                    floatDelay={0.6}
                    testId="phone-partners"
                />
            </FeatureSplit>

            {/* ==================== FEATURE 5: CHALLENGES ==================== */}
            <section
                id="challenges"
                data-testid="section-challenges"
                className="relative py-24 md:py-32"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <div className="max-w-3xl">
                        <SectionEyebrow icon={Zap}>Challenge System</SectionEyebrow>
                        <motion.h2
                            {...fadeUp}
                            className="font-display mt-5 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.02]"
                        >
                            Shared Challenges That Keep Your Community{" "}
                            <span className="bg-gradient-to-br from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">
                                Showing Up
                            </span>
                        </motion.h2>
                        <motion.p
                            {...fadeUp}
                            className="mt-5 text-white/65 text-lg md:text-xl max-w-2xl leading-relaxed"
                        >
                            Daily streaks. Weekly featured challenges. Group goals. Voro
                            turns the everyday grind into something your whole community
                            takes on together.
                        </motion.p>
                    </div>

                    <div className="mt-12">
                        <ChallengeShowcase />
                    </div>
                </div>
            </section>

            {/* ==================== FEATURE 6: PROFILE & REWARDS ==================== */}
            <FeatureSplit
                id="profile"
                testId="section-feature-profile"
                eyebrow="Profile & Rewards"
                eyebrowIcon={Crown}
                title="Celebrate Your"
                accent="Progress"
                description="Every member gets a profile that tells their story. Streaks, achievements, milestones, and shared moments — turn showing up into something worth celebrating."
                bullets={[
                    "Streaks",
                    "Achievements",
                    "Milestones",
                    "PRs",
                    "Shared moments",
                    "Training history",
                ]}
            >
                <PhoneMockup
                    src={SCREENSHOTS.athleteProfile}
                    alt="Athlete profile"
                    size="lg"
                    rotate={-3}
                    floatDelay={0.3}
                    testId="phone-profile"
                />
            </FeatureSplit>

            {/* ==================== FEATURE 7: LEADERBOARDS (COMPETITION) ==================== */}
            <FeatureSplit
                id="leaderboards"
                testId="section-feature-leaderboards"
                eyebrow="Leaderboards"
                eyebrowIcon={Trophy}
                title="A Little Friendly"
                accent="Competition"
                description="A fun way to celebrate the most consistent members of your community. Individual and gym leaderboards make showing up rewarding — without making fitness feel like a race."
                bullets={[
                    "Individual board",
                    "Gym / group board",
                    "Weekly resets",
                    "Featured challenges",
                    "Recognition for consistency",
                ]}
                reverse
            >
                <PhoneMockup
                    src={SCREENSHOTS.gymLeaderboard}
                    alt="Voro leaderboards"
                    size="lg"
                    rotate={3}
                    floatDelay={0.4}
                    testId="phone-leaderboards"
                />
            </FeatureSplit>

            {/* ==================== WHY VORO EXISTS ==================== */}
            <section
                data-testid="section-why-voro"
                className="relative py-28 md:py-40 overflow-hidden"
            >
                <div
                    className="aurora"
                    style={{
                        width: 700,
                        height: 700,
                        top: "20%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#7c3aed",
                        opacity: 0.35,
                    }}
                />
                <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
                    <SectionEyebrow icon={Heart}>Our why</SectionEyebrow>
                    <motion.h2
                        {...fadeUp}
                        className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]"
                    >
                        Most fitness apps track workouts.
                        <br />
                        <span className="bg-gradient-to-br from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                            Voro builds community.
                        </span>
                    </motion.h2>
                    <motion.div
                        {...fadeUp}
                        className="mt-10 space-y-5 text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
                    >
                        <p>People don&apos;t quit fitness because they lack information.</p>
                        <p className="text-white">They quit because they lose the people around them.</p>
                        <p>
                            Voro brings together local communities, accountability,
                            shared challenges, and recognition — so showing up becomes{" "}
                            <span className="text-violet-300 font-semibold">
                                something you do together
                            </span>
                            .
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ==================== WAITLIST CTA ==================== */}
            <section
                id="waitlist"
                data-testid="section-waitlist"
                className="relative py-24 md:py-32"
            >
                <div className="max-w-5xl mx-auto px-5 md:px-8">
                    <div className="relative overflow-hidden rounded-[2.2rem] border border-violet-400/30 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-violet-600/20 px-6 md:px-14 py-16 md:py-20 text-center">
                        <div
                            className="aurora"
                            style={{
                                width: 600,
                                height: 600,
                                top: "-40%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "#8b5cf6",
                                opacity: 0.5,
                            }}
                        />
                        <div className="relative">
                            <SectionEyebrow icon={Crown}>Founding members &amp; communities</SectionEyebrow>
                            <h2 className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
                                Be One Of The First To{" "}
                                <span className="bg-gradient-to-br from-violet-200 to-fuchsia-300 bg-clip-text text-transparent">
                                    Join Voro
                                </span>
                            </h2>
                            <p className="mt-5 text-white/75 text-lg md:text-xl max-w-2xl mx-auto">
                                Launching first in Perth for athletes, gym members, run
                                clubs, gyms, and fitness communities — then rolling out
                                nationally.
                            </p>

                            <div className="mt-10 max-w-xl mx-auto">
                                <WaitlistForm />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== FAQ ==================== */}
            <section id="faq" data-testid="section-faq" className="relative py-20 md:py-28">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <motion.div {...fadeUp} className="text-center">
                        <SectionEyebrow>FAQ</SectionEyebrow>
                        <h2 className="font-display mt-5 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.02]">
                            Everything you need to know
                        </h2>
                    </motion.div>

                    <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                        <Accordion type="single" collapsible data-testid="faq-accordion">
                            {[
                                {
                                    q: "What is Voro?",
                                    a: "Voro is a fitness platform built around local communities. Members discover their local fitness scene on a live map, join events and workouts, stay accountable with training partners, complete shared challenges, and celebrate progress — together. Leaderboards add a fun, light layer of friendly competition.",
                                },
                                {
                                    q: "When does Voro launch?",
                                    a: "Voro launches July 6th, 2026 — Perth first. Join the waitlist to be part of one of the founding communities invited before public release.",
                                },
                                {
                                    q: "Is Voro free?",
                                    a: "Voro is free for members. Founding communities get exclusive achievements and early-access features.",
                                },
                                {
                                    q: "Can gyms join?",
                                    a: "Yes — gyms are at the heart of Voro. Run your own community, host events and challenges, and engage your members through the activity dashboard.",
                                },
                                {
                                    q: "Can run clubs and studios join?",
                                    a: "Absolutely. Run clubs, pilates studios, HYROX communities, and wellness groups can all host events, build local followings, and show up on the Voro live map.",
                                },
                                {
                                    q: "How do leaderboards work?",
                                    a: "Leaderboards are a fun way to celebrate the most consistent members of your community. Earn Voro Points (VP) by logging workouts, joining events, and completing challenges — it's a recognition system, not a race.",
                                },
                                {
                                    q: "How do challenges work?",
                                    a: "Pick a featured weekly challenge or set up one for your community. Hit the goal to earn VP, unlock achievements, and celebrate the win together.",
                                },
                            ].map((item, i) => (
                                <AccordionItem
                                    key={item.q}
                                    value={`faq-${i}`}
                                    data-testid={`faq-item-${i}`}
                                    className="border-b border-white/5 last:border-b-0 px-5"
                                >
                                    <AccordionTrigger className="text-left text-white hover:no-underline py-5 text-base md:text-lg font-semibold">
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-white/70 text-[15px] leading-relaxed pb-5">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* ==================== DOWNLOAD APP placeholder anchor + final CTA ==================== */}
            <section id="download" data-testid="section-download" className="relative py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-5 md:px-8">
                    <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl p-8 md:p-14 text-center">
                        <SectionEyebrow icon={Sparkles}>Coming soon</SectionEyebrow>
                        <h2 className="font-display mt-5 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
                            Download Voro on iOS & Android
                        </h2>
                        <p className="mt-4 text-white/65 text-lg max-w-2xl mx-auto">
                            The app is rolling out to founding athletes in waves. Join the
                            waitlist now to lock in early access.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <PurpleButton
                                onClick={scrollToWaitlist}
                                testId="download-cta-button"
                            >
                                Join Waitlist <ChevronRight size={18} />
                            </PurpleButton>
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== FOOTER ==================== */}
            <footer className="relative border-t border-white/[0.06] py-12 mt-10">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-8 md:gap-10 items-start">
                    <div className="flex flex-col gap-3">
                        <VoroLogo className="h-9" />
                        <p className="text-sm text-white/50 max-w-xs">
                            The fitness platform built for local communities — where you
                            train, show up, and progress together.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 md:justify-center">
                        <a
                            href="https://www.instagram.com/vorofitness/"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-instagram"
                            className="inline-flex items-center gap-1.5 text-sm text-white/65 hover:text-white"
                        >
                            <Instagram size={14} /> Instagram
                        </a>
                        <a
                            href="https://www.tiktok.com/@vorofitnessapp"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-tiktok"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            TikTok
                        </a>
                        <a
                            href="mailto:hello@voro.au"
                            data-testid="footer-contact"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            hello@voro.au
                        </a>
                        <a
                            href="tel:+61466960644"
                            data-testid="footer-phone"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            0466 960 644
                        </a>
                        <a
                            href="/privacy"
                            data-testid="footer-privacy"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            Privacy Policy
                        </a>
                        <a
                            href="/terms"
                            data-testid="footer-terms"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            Terms of Service
                        </a>
                        <a
                            href="/support"
                            data-testid="footer-support"
                            className="text-sm text-white/65 hover:text-white"
                        >
                            Support
                        </a>
                    </div>

                    <div className="text-sm text-white/40 md:text-right">
                        © {new Date().getFullYear()} Voro. Fitness Is Better Together.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
